<template>
    <!-- Fullscreen Section detail overlay. Teleported to <body> so it escapes the page's
         stacking/overflow context, and pinned below the fixed top navigation bar. Which Section is
         open is a route, resolved by the parent into the `section` prop, so the browser's own back
         button closes the overlay and nothing here touches history. -->
    <Teleport to="body">
        <FadeTransition>
            <div
                v-if="section"
                class="fixed inset-x-0 bottom-0 z-[1020] flex flex-col bg-white"
                style="top: 100px"
                role="dialog"
                aria-modal="true"
            >
                <!-- Shared header: Section name, meta line, alarm chip, named prev/next, Escape and
                     close. Owns Escape and the Arrow keys itself, so the overlay registers no
                     keyboard handler of its own. -->
                <div class="border-b border-gray-200">
                    <SectionDetailHeader
                        :section="section"
                        :sections="sections"
                        @close="emit('close')"
                        @navigate="emit('navigate', $event)"
                        @handle-alarm="emit('handleAlarm', $event)"
                    />
                </div>

                <!-- The Productivity Chart spans however many Production Groups the (currently
                     fixed) range covers - not just the running one - so a Section standing empty
                     between Production Groups still opens showing its previous periods, not a placeholder. -->
                <!-- The panel fills the overlay rather than sitting at its natural height in a
                     scroll container: the chart is the only thing here, so leaving a screenful of
                     grey under it wastes exactly the vertical room its stacked bands need. -->
                <div class="flex min-h-0 flex-1 flex-col overflow-y-auto bg-gray-50 p-6">
                    <div class="mx-auto flex min-h-0 w-full max-w-7xl flex-1 flex-col">
                        <ProductivityChart
                            :section-id="sectionId"
                            :production-group-start="section.productionGroupStart"
                            :production-group-history="productionGroupHistory"
                            :production-group-history-unavailable="productionGroupHistoryUnavailable"
                        />
                    </div>
                </div>
            </div>
        </FadeTransition>
    </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import FadeTransition from '@/components/transitions/FadeTransition.vue';
import type { ProductionGroup } from '@/types/ProductionGroup';
import ProductivityChart from './ProductivityChart.vue';
import SectionDetailHeader from './SectionDetailHeader.vue';
import type { SectionRow } from './sectionRow';

const props = defineProps<{
    section: SectionRow | null;
    // All rows whose chart icon can open this overlay, in the page's display order — drives the
    // shared header's prev/next navigation. The parent stays in control: navigation only emits the
    // target row.
    sections: SectionRow[];
    // The open Section's Production Group history, read by the page above, and whether that read
    // failed. Both passed straight through to ProductivityChart: an empty list means the Section has
    // no earlier period, and the flag is what separates that from not knowing.
    productionGroupHistory: ProductionGroup[];
    productionGroupHistoryUnavailable: boolean;
}>();

const emit = defineEmits<{
    close: [];
    navigate: [section: SectionRow];
    handleAlarm: [section: SectionRow];
}>();

const sectionId = computed(() => props.section?.id ?? null);
</script>
